import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  Star,
  MoreVertical,
  Phone,
  Mail,
  MapPin,
  Building2,
  Pencil,
  Eye,
  Trash2,
  Flag,
  CheckCircle,
  XCircle
} from 'lucide-react';

const SupplierCard = ({
  supplier,
  size = 'medium', // small, medium, large
  showActions = true,
  onEdit = null,
  onView = null,
  onDelete = null,
  onClick = null
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  if (!supplier) {
    return (
      <Alert>
        <AlertDescription>No supplier data available</AlertDescription>
      </Alert>
    );
  }

  const handleAction = (action) => {
    setMenuOpen(false);
    switch (action) {
      case 'edit':
        onEdit && onEdit(supplier);
        break;
      case 'view':
        onView && onView(supplier);
        break;
      case 'delete':
        onDelete && onDelete(supplier);
        break;
      default:
        break;
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'UN';
  };

  const getSupplierTypeVariant = (type) => {
    switch (type?.toLowerCase()) {
      case 'manufacturer': return 'default';
      case 'distributor': return 'secondary';
      case 'service': return 'outline';
      default: return 'outline';
    }
  };

  const getSupplierTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'manufacturer': return '制造商';
      case 'distributor': return '分销商';
      case 'service': return '服务商';
      default: return '其他';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      notation: amount > 100000 ? 'compact' : 'standard'
    }).format(amount || 0);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${star <= rating ? 'text-warning fill-yellow-500' : 'text-muted-foreground'}`}
          />
        ))}
        <span className="text-xs text-muted-foreground ml-1">({rating}/5)</span>
      </div>
    );
  };

  if (size === 'small') {
    return (
      <Card
        className={`${onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md transition-all duration-200' : ''}`}
        onClick={onClick ? () => onClick(supplier) : undefined}
      >
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white ${
                  supplier.is_preferred ? 'bg-warning/100' : 'bg-primary/100'
                }`}
              >
                {getInitials(supplier.supplier_name)}
              </div>
              <div>
                <p className="text-sm font-medium truncate">{supplier.supplier_name}</p>
                <p className="text-xs text-muted-foreground">{supplier.contact_person}</p>
              </div>
            </div>
            <div className="text-right flex items-center gap-2">
              <Badge variant={getSupplierTypeVariant(supplier.supplier_type)}>
                {getSupplierTypeLabel(supplier.supplier_type)}
              </Badge>
              {supplier.is_active ? (
                <CheckCircle className="h-4 w-4 text-success" />
              ) : (
                <XCircle className="h-4 w-4 text-destructive" />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`${size === 'large' ? 'shadow-md' : 'shadow-sm'} ${
        onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200' : ''
      }`}
      onClick={onClick ? () => onClick(supplier) : undefined}
    >
      <CardContent className={size === 'large' ? 'p-6' : 'p-4'}>
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div
                className={`${size === 'large' ? 'w-14 h-14' : 'w-12 h-12'} rounded-full flex items-center justify-center text-white font-medium ${
                  supplier.is_active ? 'bg-primary/100' : 'bg-muted/60'
                }`}
              >
                {getInitials(supplier.supplier_name)}
              </div>
              {supplier.is_preferred && (
                <Flag className="absolute -bottom-1 -right-1 h-4 w-4 text-warning" />
              )}
            </div>

            <div>
              <h3 className={`${size === 'large' ? 'text-lg' : 'text-base'} font-bold mb-1`}>
                {supplier.supplier_name}
              </h3>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge variant={getSupplierTypeVariant(supplier.supplier_type)}>
                  {getSupplierTypeLabel(supplier.supplier_type)}
                </Badge>
                {supplier.is_preferred && (
                  <Badge variant="outline" className="bg-warning/10 text-warning border-yellow-300">
                    <Star className="h-3 w-3 mr-1" />
                    优选
                  </Badge>
                )}
                <Badge variant={supplier.is_active ? 'outline' : 'destructive'}>
                  {supplier.is_active ? '启用' : '停用'}
                </Badge>
              </div>
              {renderStars(supplier.rating || 3)}
            </div>
          </div>

          {showActions && (
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleAction('view')}>
                  <Eye className="h-4 w-4 mr-2" />
                  查看详情
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAction('edit')}>
                  <Pencil className="h-4 w-4 mr-2" />
                  编辑供应商
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleAction('delete')} className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  删除供应商
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Contact Information */}
        <div className="mb-4">
          <h4 className="text-sm font-medium mb-2">联系信息</h4>
          <div className="space-y-1">
            {supplier.contact_person && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>{supplier.contact_person}</span>
              </div>
            )}
            {supplier.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{supplier.phone}</span>
              </div>
            )}
            {supplier.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="truncate">{supplier.email}</span>
              </div>
            )}
            {supplier.city && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{supplier.city}, {supplier.province}</span>
              </div>
            )}
          </div>
        </div>

        {/* Categories */}
        {supplier.categories && supplier.categories.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">业务类别</h4>
            <div className="flex gap-1 flex-wrap">
              {supplier.categories.slice(0, 3).map((category, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {category}
                </Badge>
              ))}
              {supplier.categories.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{supplier.categories.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Performance Metrics (if available) */}
        {supplier.performance_metrics && size === 'large' && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">绩效表现</h4>
            <div className="space-y-2">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm">交付准时率</span>
                  <span className="text-sm font-medium">
                    {(supplier.performance_metrics.delivery_performance || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full h-1 bg-muted/80 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      supplier.performance_metrics.delivery_performance > 80 ? 'bg-success/100' : 'bg-warning/100'
                    }`}
                    style={{ width: `${supplier.performance_metrics.delivery_performance || 0}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm">质量评分</span>
                  <span className="text-sm font-medium">
                    {(supplier.performance_metrics.quality_score || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full h-1 bg-muted/80 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      supplier.performance_metrics.quality_score > 85 ? 'bg-success/100' : 'bg-primary/100'
                    }`}
                    style={{ width: `${supplier.performance_metrics.quality_score || 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Certifications */}
        {supplier.certifications && supplier.certifications.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">资质认证</h4>
            <div className="flex gap-1 flex-wrap">
              {supplier.certifications.slice(0, 4).map((cert, index) => (
                <Badge key={index} variant="outline" className="text-xs border-primary/30 text-primary">
                  {cert}
                </Badge>
              ))}
              {supplier.certifications.length > 4 && (
                <Badge variant="secondary" className="text-xs">
                  +{supplier.certifications.length - 4}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Financial Info */}
        {size === 'large' && (
          <>
            <Separator className="my-4" />
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">信用额度</p>
                <p className="font-medium">{formatCurrency(supplier.credit_limit)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">结算货币</p>
                <p className="font-medium">{supplier.currency || 'CNY'}</p>
              </div>
            </div>
          </>
        )}

        {/* Action Buttons (for large cards) */}
        {size === 'large' && showActions && (
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onView && onView(supplier);
              }}
            >
              查看详情
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit && onEdit(supplier);
              }}
            >
              编辑
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SupplierCard;
